import { Children } from "react";
import { Field, FormGroup } from "../../../components/form";
import * as Yup from "yup";

function calculateDisabledProp(context: any, model: any): boolean {
  return context.disabled || model.doesChildAttend == false || model.doesChildAttend == undefined;
}

function calculateValue(model: any, valueInstance: any, returnValue: any): any {
  if (model.doesChildAttend == false || model.doesChildAttend == undefined) {
    return returnValue;
  }
  return valueInstance;
}

function generateFormFields() {
  const daysOfTheWeek = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
  const formgroup: FormGroup[] = [];

  daysOfTheWeek.map((day, i) => {
    formgroup.push({
      type: "formGroup",
      key: day,
      customWrapper: ({ children }: any) => (
        <>
          <td key={day + "_" + i}>{day}</td>
          {Children.map(children, (child) => (
            <td key={day + "_" + i}>{child}</td>
          ))}
        </>
      ),
      fields: dayForm,
    });
  });

  return formgroup;
}

const dayForm: Field[] = [
  {
    type: "field",
    key: "doesChildAttend",
    fieldType: "checkbox",
  },
  {
    type: "field",
    key: "startTime",
    fieldType: "time",
    expressionProps: {
      disabled: (context, values, model) => {
        return calculateDisabledProp(context, model);
      },
    },
    calculatedValue: (context, values, model) => {
      return calculateValue(model, model.startTime, null);
    },
    validation: Yup.string().when(["doesChildAttend", "endTime"], ([doesChildAttend, endTime], schema) => {
      if (doesChildAttend != true) {
        return schema.notRequired();
      }
      if (endTime == null) {
        return schema.required("A response is required");
      } else {
        return schema
          .required("A response is required")
          .test("start-before-end", "The start time may not be later than the end time", function (startTime) {
            // if either value is missing, let "required" handle it
            if (!startTime || !endTime) return true;

            return startTime <= endTime;
          });
      }
    }),
  },
  {
    type: "field",
    key: "endTime",
    fieldType: "time",
    expressionProps: {
      disabled: (context, values, model) => {
        return calculateDisabledProp(context, model);
      },
    },
    calculatedValue: (context, values, model) => {
      return calculateValue(model, model.endTime, null);
    },
    validation: Yup.string().when(["doesChildAttend"], ([doesChildAttend], schema) => {
      if (doesChildAttend != true) {
        return schema.notRequired();
      }
      return schema.required("A response is required");
    }),
  },
  {
    type: "field",
    key: "groupNames",
    fieldType: "textarea",
    expressionProps: {
      disabled: (context, values, model) => {
        return calculateDisabledProp(context, model);
      },
    },
    calculatedValue: (context, values, model) => {
      return calculateValue(model, model.groupNames, "");
    },
    validation: Yup.string().when(["doesChildAttend"], ([doesChildAttend], schema) => {
      if (doesChildAttend != true) {
        return schema.notRequired();
      }
      return schema.required("A response is required");
    }),
  },
  {
    type: "field",
    key: "childrenAttendance",
    fieldType: "number",
    expressionProps: {
      disabled: (context, values, model) => {
        return calculateDisabledProp(context, model);
      },
    },
    calculatedValue: (context, values, model) => {
      return calculateValue(model, model.childrenAttendance, "");
    },
    validation: Yup.number().when(["doesChildAttend"], ([doesChildAttend], schema) => {
      if (doesChildAttend != true) {
        return schema.notRequired();
      }
      return schema
        .required("A response is required")
        .min(1, "At least one child must be in the group")
        .max(100, "Maximum of 100 children allowed in a group");
    }),
  },
  {
    type: "field",
    key: "advisory",
    fieldType: "advisory",
    label: "*",
  },
];

export const groupInformationForm: FormGroup = {
  type: "formGroup",
  fields: [
    {
      type: "field",
      key: "advisory1",
      fieldType: "advisory",
      label:
        "Please provide information about the hours the child attends on each day of the week and the group name for each day. If there are rotational groups please note all groups that attend on that day.",
    },
    {
      type: "field",
      key: "advisory2",
      fieldType: "advisory",
      props: {
        type: "warning",
        Body: () => (
          <>
            <p className="list-none pl-2 m-0">
              If multiple KIS applications are received from this kindergarten, this information is used to map which children attend the service at
              the same time as this child. Please ensure that the group names you use are consistent across the service, or we may be required to
              contact you for additional information which can delay processing.
            </p>
          </>
        ),
      },
      label: "",
    },
    {
      type: "formGroup",
      key: "groupInformationWeeklyData",
      customWrapper: ({ children }: any) => (
        <table className="group-info" key="gi_WeeklyData">
          <thead>
            <tr>
              <th></th>
              <th>Does the child attend?</th>
              <th>Start time</th>
              <th>End time</th>
              <th>
                Group name. <br />
                If multiple groups attend with this child (i.e. a rotational model) please list all group names separately, separated by a comma.
              </th>
              <th>How many children in total attend this group session on this day</th>
            </tr>
          </thead>
          <tbody>
            {Children.map(children, (child, i) => (
              <tr key={"weekdata_" + i}>{child}</tr>
            ))}
          </tbody>
        </table>
      ),
      fields: generateFormFields(),
    },
    {
      type: "field",
      key: "atleastOneDayOfTheWeek",
      fieldType: "error",
      validation: Yup.boolean().test("atleastOneDayOfTheWeek", "A response is required", function () {
        const { groupInformationWeeklyData } = this.parent;
        return (
          groupInformationWeeklyData.Monday.doesChildAttend ||
          groupInformationWeeklyData.Tuesday.doesChildAttend ||
          groupInformationWeeklyData.Wednesday.doesChildAttend ||
          groupInformationWeeklyData.Thursday.doesChildAttend ||
          groupInformationWeeklyData.Friday.doesChildAttend ||
          groupInformationWeeklyData.Saturday.doesChildAttend ||
          groupInformationWeeklyData.Sunday.doesChildAttend
        );
      }),
    },

    {
      type: "formGroup",
      classNameOpt: "staffRatio",
      fields: [
        {
          type: "field",
          key: "advisory3",
          fieldType: "advisory",
          label: "Staff to Child Ratio",
        },
        {
          type: "field",
          fieldType: "number",
          key: "staffAmt",
          label: "Staff",
          props: {
            className: "p-inputtext p-component",
          },
          validation: Yup.number()
            .required("A response is required")
            .max(100, "Staff number must be between 1 and 100")
            .min(1, "Staff number must be between 1 and 100"),
        },
        {
          type: "field",
          fieldType: "number",
          key: "childrenAmt",
          label: "Children",
          validation: Yup.number()
            .required("A response is required")
            .max(100, "Children number must be between 1 and 100")
            .min(1, "Children number must be between 1 and 100"),
        },
        {
          type: "field",
          key: "advisory4",
          fieldType: "advisory",
          props: {
            type: "fieldadv",
          },
          label:
            "Show the ratio of staff (teachers and educators) to children in the room e.g.: 1 staff to 11 children. Note: Additional Assistants are not counted as staff for this ratio.",
        },
      ],
    },
    {
      type: "field",
      fieldType: "radio",
      key: "doesRotationalGroups",
      label: "Does the program run rotational groups?",
      extendedlabel: (
        <p className="list-none pl-2 m-0">
          A rotational model is where a number of smaller sub-groups are brought together to create a larger group. Each child is allocated with other
          children to a small group. These children always attend together throughout the week and will attend with other groups during the week.
          <br />
          Factsheets about the rotational model are available on the department’s{" "}
          <a
            className="underline"
            href="https://www.education.vic.gov.au/Documents/childhood/providers/edcare/change-management-info-sheets-your-guide-to-rotational-models.pdf"
            target="_blank"
            rel="noreferrer">
            website<i className="pi pi-external-link ml-1"></i>
          </a>
          .
        </p>
      ),
      validation: Yup.string().required("A response is required"),
      props: {
        options: [
          {
            label: "Yes",
            value: 1,
          },
          {
            label: "No",
            value: 0,
          },
        ],
      },
    },
    {
      type: "field",
      fieldType: "radio",
      label: "Do these group/s operate bush kinder sessions during the year?",
      extendedlabel: (
        <p className="list-none pl-2 m-0">
          If the kinder currently operates bush kinder sessions for this group/s, or is planning to during the year, you will be required to upload a
          Bush Kinder Risk Minimisation Plan at the end of this application.
          <br />
          Bush kinder includes beach kinder and kinder programs delivered in other outdoor settings external to the kindergarten, as part of a “bush
          kinder” program.
        </p>
      ),
      key: "doesBushKinderSessions",
      validation: Yup.string().required("A response is required"),
      props: {
        options: [
          {
            label: "Yes",
            value: 1,
          },
          {
            label: "No/Unsure",
            value: 0,
          },
        ],
      },
    },
    {
      type: "field",
      fieldType: "advisory",
      key: "advisory",
      hideExpression: (formContext, values, model) => !model.doesBushKinderSessions,
      label: (
        <>
          <label>Over what period does bush kinder operate?</label>
        </>
      ),
    },
    {
      type: "field",
      fieldType: "calendar",
      key: "startDate",
      label: "Start Date",
      hideExpression: (formContext, values, model) => !model.doesBushKinderSessions,
      validation: Yup.date().when(["endDate"], ([endDate], schema) => {
        if (endDate) {
          return schema.required("A response is required").max(endDate, "The start date must be earlier than the end date");
        }
        return schema.required("A response is required");
      }),
    },
    {
      type: "field",
      fieldType: "calendar",
      key: "endDate",
      label: "End date",
      hideExpression: (formContext, values, model) => !model.doesBushKinderSessions,
      validation: Yup.date().required("A response is required"),
    },
    {
      type: "field",
      fieldType: "textarea",
      props: {
        rows: 5,
        col: 30,
        autoResize: true,
      },
      key: "infoOfBushKinderSessions",
      label:
        "If there is any other information about the operation of bush kinder sessions that is important for the KIS assessment team to understand, please include it here.",
      hideExpression: (formContext, values, model) => !model.doesBushKinderSessions,
    },
    {
      type: "field",
      fieldType: "radio",
      key: "hasOtherSupport",
      label:
        "Thinking only about the year and group that relate to this application, has the group been allocated KIS support from another application?",
      validation: Yup.string().required("A response is required"),
      props: {
        options: [
          {
            label: "Yes",
            value: 1,
          },
          {
            label: "No",
            value: 0,
          },
        ],
      },
    },
    {
      type: "formGroup",
      label: "What KIS support has been allocated to this year? Select all that apply.",
      key: "kisSupport",
      hideExpression: (formContext, values) => !values.hasOtherSupport,
      fields: [
        {
          type: "field",
          fieldType: "checkbox",
          key: "specialistConsultancy",
          label: "Specialist consultancy",
        },
        {
          type: "field",
          fieldType: "checkbox",
          key: "additionalAssistant",
          label: "Additional assistant",
        },
        {
          type: "field",
          fieldType: "checkbox",
          key: "specialistTraining",
          label: "Specialist training",
        },
        {
          type: "field",
          fieldType: "checkbox",
          key: "minorBuildingModifications",
          label: "Minor building modifications",
        },
      ],
      test: [
        "atleastOneKisSupport",
        "A response is required",
        (value) => value.minorBuildingModifications || value.specialistTraining || value.additionalAssistant || value.specialistConsultancy,
      ],
    },
    {
      type: "formGroup",
      label:
        "Thinking only about the year and group that relate to this application, what supports are your kindergarten group accessing from other programs? Select all that apply.",
      key: "otherSupport",
      fields: [
        {
          type: "field",
          key: "schoolReadinessFunding",
          fieldType: "checkbox",
          label: "School Readiness Funding (SRF)",
        },
        {
          type: "field",
          fieldType: "checkbox",
          key: "flexibleSupportPackages",
          label: "Flexible Support Packages (FSP)",
        },
        {
          type: "field",
          fieldType: "checkbox",
          key: "inclusionSupportPrograms",
          label: "Inclusion Support Programs (ISP)",
        },
        {
          type: "field",
          fieldType: "checkbox",
          key: "preschoolFieldOfficer",
          label: "Preschool Field Officer (PSFO)",
        },
        {
          type: "field",
          fieldType: "checkbox",
          key: "earlyChildhoodInterventionServices",
          label: "Early Childhood Intervention Services (ECIS)",
        },
        {
          type: "field",
          fieldType: "checkbox",
          key: "earlyChildhoodLearningAdvisors",
          label: "LOOKOUT Early Childhood Learning Advisors (ECLAs)",
        },
        {
          type: "field",
          fieldType: "checkbox",
          key: "accessToEarlyLearning",
          label: "Access to Early Learning (AEL)",
        },
        {
          type: "field",
          fieldType: "checkbox",
          key: "mentalHealthInEarlyChildhood",
          label: "Mental Health in Early Childhood (HEC) Program / Early Childhood Mental Health Consultation",
        },
        {
          type: "field",
          fieldType: "checkbox",
          key: "supportFromOtherProgramsOther",
          label: "Other - please specify",
        },
        {
          type: "field",
          fieldType: "checkbox",
          key: "supportFromOtherPrograms",
          label: "None of the above",
        },
      ],
      test: [
        "atleastOneOtherSupport",
        "A response is required",
        (value) =>
          value.supportFromOtherPrograms ||
          value.supportFromOtherProgramsOther ||
          value.mentalHealthInEarlyChildhood ||
          value.accessToEarlyLearning ||
          value.earlyChildhoodLearningAdvisors ||
          value.preschoolFieldOfficer ||
          value.inclusionSupportPrograms ||
          value.flexibleSupportPackages ||
          value.schoolReadinessFunding ||
          value.earlyChildhoodInterventionServices,
      ],
    },

    {
      type: "field",
      fieldType: "textarea",
      key: "otherPrograms",
      label: "Please specify the other program/s you have received support from this year",
      hideExpression: (formContext, values) => !values.otherSupport.supportFromOtherProgramsOther,
      validation: Yup.string().trim().required("A response is required"),
    },
    {
      type: "field",
      fieldType: "textarea",
      key: "additionalEducators",
      label:
        "Please describe the title and role of any additional educators in attendance that assist the teaching team, such as additional assistants placed through the School Readiness Funding Program or a Flexible Support Package.",
    },
  ],
};
